import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, FileDown, Trash2, Shield } from 'lucide-react';
import { useEntgeltEmbed } from '../../context/EntgeltEmbedContext';
import EntgeltBeratungsCta from './EntgeltBeratungsCta';

const ET_PATH = '/resources/entgelttransparenz';

/**
 * Wie Original-Entgelt-Sidebar (inkl. Beratungs-CTA), plus „Zurück zum Portal“.
 * Optik: Portal-Tokens (Slate, Navy/Coral, Abstände).
 */
function EntgeltSidebarPanel({ variant, dashboardPath = '/' }) {
  const location = useLocation();
  const ctx = useEntgeltEmbed();
  const [rows, setRows] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);

  const post = useCallback(
    (msg) => {
      ctx?.postToEmbed?.(msg);
    },
    [ctx],
  );

  useEffect(() => {
    if (location.pathname !== ET_PATH || !ctx) return undefined;

    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      const win = ctx.iframeRef?.current?.contentWindow;
      if (!win || e.source !== win) return;
      const d = e.data;
      if (d?.type === 'et-saved-jobs') {
        setRows(Array.isArray(d.items) ? d.items : []);
        setActiveIdx(d.activeSavedIndex ?? null);
      }
      if (d?.type === 'et-embed-ready') {
        post({ type: 'et-host-request-state' });
      }
      if (d?.type === 'et-embed-sidebar-collapsed') {
        ctx.setEmbedSidebarCollapsed?.(Boolean(d.collapsed));
      }
    };

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [ctx, location.pathname, post]);

  useEffect(() => {
    if (location.pathname === ET_PATH && ctx?.embedReady) {
      post({ type: 'et-host-request-state' });
    }
  }, [ctx?.embedReady, location.pathname, post]);

  if (location.pathname !== ET_PATH || !ctx) return null;

  const neueStelle = () => post({ type: 'et-host-new' });
  const openJob = (index) => post({ type: 'et-host-open', index });
  const exportWord = (index, ev) => {
    ev.stopPropagation();
    post({ type: 'et-host-export-word', index });
  };
  const removeJob = (index, ev) => {
    ev.stopPropagation();
    post({ type: 'et-host-delete', index });
  };

  const collapsed = ctx.embedSidebarCollapsed === true;

  if (variant === 'resources') {
    if (collapsed) {
      return (
        <div className="flex flex-col flex-1 min-h-0 w-full items-center gap-1.5 py-1">
          <Link
            to={dashboardPath}
            title="Zurück zum Portal"
            className="p-2 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
          <button
            type="button"
            title="Neue Stelle beschreiben"
            onClick={neueStelle}
            className="p-2 rounded-lg text-white border border-white/15 bg-white/10 hover:bg-white/15"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
          </button>
          <div className="flex-1 min-h-[1rem] shrink" aria-hidden />
          <a
            href="/entgelttransparenz/datenschutz.html"
            target="_blank"
            rel="noopener noreferrer"
            title="Datenschutz"
            className="p-2 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white"
          >
            <Shield className="h-[15px] w-[15px] shrink-0" aria-hidden />
          </a>
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0 w-full">
        <div className="shrink-0">
          <Link
            to={dashboardPath}
            className="flex items-center py-2.5 pr-3 text-sm font-medium rounded-lg transition-all duration-150 border-l-2 border-transparent pl-2.5 text-slate-400 hover:bg-white/10 hover:text-slate-200"
          >
            <ArrowLeft className="h-4 w-4 mr-3 shrink-0" aria-hidden />
            <span>Zurück zum Portal</span>
          </Link>

          <div className="mx-3 h-px bg-white/10 my-3" aria-hidden />

          <div className="px-3 pb-3">
            <h2 className="text-[1.05rem] font-bold text-white leading-snug tracking-tight">
              Stellenbeschreibungen
            </h2>
            <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
              Richtlinienkonform nach EU 2023/970
            </p>
          </div>

          <div className="px-3 mb-2">
            <button
              type="button"
              onClick={neueStelle}
              className="w-full flex items-center justify-start gap-2 py-2 px-2.5 text-xs font-semibold rounded-lg bg-white/10 text-white border border-white/10 hover:bg-white/15 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
              <span className="leading-snug">Neue Stelle beschreiben</span>
            </button>
          </div>
        </div>

        <nav
          className="scrollbar-minimal flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 space-y-1.5 mb-2"
          aria-label="Gespeicherte Stellen"
        >
          {rows.length === 0 && (
            <p className="text-xs text-slate-500 px-1 py-2 leading-relaxed">Noch keine Stellen erstellt</p>
          )}
          {rows.map((row) => {
            const active = activeIdx === row.index;
            return (
              <div
                key={row.index}
                className={[
                  'rounded-lg border transition-colors',
                  active
                    ? 'border-l-2 border-hrf-coral border-y border-r border-white/10 bg-white/10'
                    : 'border border-transparent hover:bg-white/5',
                ].join(' ')}
              >
                <button
                  type="button"
                  onClick={() => openJob(row.index)}
                  className="w-full text-left px-2.5 py-2"
                >
                  <div className="text-sm font-medium text-white leading-snug truncate">{row.job_title}</div>
                  <div className="text-[11px] text-slate-400 truncate mt-0.5">
                    {[row.job_family, row.level].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{row.status}</div>
                </button>
                <div className="flex items-center gap-0.5 px-1 pb-1.5 justify-end">
                  <button
                    type="button"
                    title="Als Word herunterladen"
                    onClick={(e) => exportWord(row.index, e)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <FileDown className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Stelle löschen"
                    onClick={(e) => removeJob(row.index, e)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-300 hover:bg-white/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 px-3 pt-3 pb-2 border-t border-white/10 space-y-3">
          <EntgeltBeratungsCta variant="resources" />
          <a
            href="/entgelttransparenz/datenschutz.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-2 px-2 text-sm font-medium rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Shield className="h-[15px] w-[15px] shrink-0 opacity-90" aria-hidden />
            Datenschutz
          </a>
        </div>
      </div>
    );
  }

  /* klassische helle Shell – dieselbe inhaltliche Struktur */
  if (collapsed) {
    return (
      <div className="flex flex-col flex-1 min-h-0 w-full items-center gap-1.5 py-1">
        <Link
          to={dashboardPath}
          title="Zurück zum Portal"
          className="p-2 rounded-lg text-gray-700 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        </Link>
        <button
          type="button"
          title="Neue Stelle beschreiben"
          onClick={neueStelle}
          className="p-2 rounded-lg bg-hrf-navy/10 text-hrf-navy border border-hrf-navy/15 hover:bg-hrf-navy/15"
        >
          <Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
        </button>
        <div className="flex-1 min-h-[1rem] shrink" aria-hidden />
        <a
          href="/entgelttransparenz/datenschutz.html"
          target="_blank"
          rel="noopener noreferrer"
          title="Datenschutz"
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
        >
          <Shield className="h-[15px] w-[15px] shrink-0" aria-hidden />
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      <div className="shrink-0">
        <Link
          to={dashboardPath}
          className="flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-800 hover:bg-gray-100 mb-3"
        >
          <ArrowLeft className="h-4 w-4 mr-3 shrink-0" aria-hidden />
          Zurück zum Portal
        </Link>

        <div className="px-3 pb-3">
          <h2 className="text-base font-bold text-gray-900 leading-snug">Stellenbeschreibungen</h2>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
            Richtlinienkonform nach EU 2023/970
          </p>
        </div>

        <div className="px-3 mb-2">
          <button
            type="button"
            onClick={neueStelle}
            className="w-full flex items-center justify-start gap-2 py-2 px-2.5 text-xs font-semibold rounded-lg bg-hrf-navy/10 text-hrf-navy border border-hrf-navy/15 hover:bg-hrf-navy/15 transition-colors"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="leading-snug text-left">Neue Stelle beschreiben</span>
          </button>
        </div>
      </div>

      <nav
        className="scrollbar-minimal flex-1 min-h-0 overflow-y-auto px-3 space-y-1 mb-2"
        aria-label="Gespeicherte Stellen"
      >
        {rows.length === 0 && (
          <p className="text-xs text-gray-500 px-1 py-2 leading-relaxed">Noch keine Stellen erstellt</p>
        )}
        {rows.map((row) => {
          const active = activeIdx === row.index;
          return (
            <div
              key={row.index}
              className={[
                'rounded-lg border',
                active
                  ? 'border-l-4 border-l-hrf-coral bg-hrf-navy/[0.06] border-hrf-navy/10'
                  : 'border-gray-100 bg-white hover:bg-gray-50',
              ].join(' ')}
            >
              <button type="button" onClick={() => openJob(row.index)} className="w-full text-left px-2 py-2">
                <div className="text-sm font-medium text-gray-900 truncate">{row.job_title}</div>
                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                  {[row.job_family, row.level].filter(Boolean).join(' · ') || '—'}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">{row.status}</div>
              </button>
              <div className="flex justify-end gap-0.5 px-1 pb-1">
                <button
                  type="button"
                  title="Als Word herunterladen"
                  onClick={(e) => exportWord(row.index, e)}
                  className="p-1 text-gray-500 hover:text-hrf-navy"
                >
                  <FileDown className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  title="Stelle löschen"
                  onClick={(e) => removeJob(row.index, e)}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="shrink-0 px-3 pt-3 pb-2 border-t border-gray-200 space-y-3">
        <EntgeltBeratungsCta variant="classic" />
        <a
          href="/entgelttransparenz/datenschutz.html"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 py-2 px-2 text-sm text-gray-800 rounded-lg hover:bg-gray-100"
        >
          <Shield className="h-[15px] w-[15px] shrink-0" aria-hidden />
          Datenschutz
        </a>
      </div>
    </div>
  );
}

export default EntgeltSidebarPanel;
